import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import { currentLocale } from '../../i18n';

const RARITY_COLORS = {
  consumer: '#b0c3d9',
  industrial: '#5e98d9',
  mil_spec: '#4b69ff',
  restricted: '#8847ff',
  classified: '#d32ce6',
  covert: '#eb4b4b',
  contraband: '#e4ae39',
};

const CONDITION_LABELS = {
  fn: 'Factory New',
  mw: 'Minimal Wear',
  ft: 'Field-Tested',
  ww: 'Well-Worn',
  bs: 'Battle-Scarred',
};

/**
 * Admin panel: skin drop pool and drop history management.
 */
export default function AdminDrops({ perms }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('pool');

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.navDrops')}</h1>

      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'pool' ? 'active' : ''}`} onClick={() => setTab('pool')}>
          {t('admin.dropTabPool')}
        </button>
        <button className={`admin-tab ${tab === 'config' ? 'active' : ''}`} onClick={() => setTab('config')}>
          {t('admin.dropTabConfig')}
        </button>
        <button className={`admin-tab ${tab === 'trades' ? 'active' : ''}`} onClick={() => setTab('trades')}>
          {t('admin.dropTabTrades')}
        </button>
        <button className={`admin-tab ${tab === 'inventory' ? 'active' : ''}`} onClick={() => setTab('inventory')}>
          {t('admin.dropTabInventory')}
        </button>
        <button className={`admin-tab ${tab === 'exclusions' ? 'active' : ''}`} onClick={() => setTab('exclusions')}>
          {t('admin.dropTabExclusions')}
        </button>
      </div>

      {tab === 'pool' && <SkinPool perms={perms} />}
      {tab === 'config' && <DropConfig perms={perms} />}
      {tab === 'trades' && <Trades perms={perms} />}
      {tab === 'inventory' && <BotInventory perms={perms} />}
      {tab === 'exclusions' && <Exclusions perms={perms} />}
    </div>
  );
}

function SkinPool({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [skins, setSkins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add manual form
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addCondition, setAddCondition] = useState('ft');
  const [addRarity, setAddRarity] = useState('mil_spec');
  const [addStattrak, setAddStattrak] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [addImageUrl, setAddImageUrl] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  const fetchSkins = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/skins`, { headers: getAuthHeaders() });
      if (r.ok) {
        const data = await r.json();
        setSkins(Array.isArray(data) ? data : data.results || []);
      }
    } catch {} finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchSkins(); }, [fetchSkins]);

  const handleDelete = async (skinId) => {
    if (!window.confirm(t('admin.dropDeleteSkinConfirm'))) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/skins/${skinId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (r.ok) fetchSkins();
      else setError(t('admin.dropDeleteFailed'));
    } catch { setError(t('settings.networkError')); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError('');
    try {
      const body = {
        name: addName,
        condition: addCondition,
        rarity: addRarity,
        stattrak: addStattrak,
        value_sek: addValue ? parseFloat(addValue) : null,
        image_url: addImageUrl || null,
      };
      const r = await fetch(`${API_URL}/api/admin/drops/skins`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const data = await r.json();
        setAddError(data.error || data.detail || t('admin.dropAddFailed'));
      } else {
        setShowAdd(false);
        setAddName(''); setAddValue(''); setAddImageUrl('');
        fetchSkins();
      }
    } catch { setAddError(t('settings.networkError')); }
    finally { setAddLoading(false); }
  };

  return (
    <>
      {error && <div className="admin-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      {perms.hasAnyCommand('drop.add') && (
        <div style={{ marginBottom: '1rem' }}>
          {!showAdd ? (
            <button className="admin-btn" onClick={() => setShowAdd(true)}>{t('admin.dropAddManual')}</button>
          ) : (
            <div className="admin-card">
              <div className="admin-card-title">{t('admin.dropAddSkin')}</div>
              <form onSubmit={handleAdd}>
                <div className="admin-row">
                  <label className="admin-label">{t('admin.nameLabel')}</label>
                  <input className="admin-input" value={addName} onChange={(e) => setAddName(e.target.value)} required placeholder="AK-47 | Redline" />
                </div>
                <div className="admin-row">
                  <label className="admin-label">Condition</label>
                  <select className="admin-input" value={addCondition} onChange={(e) => setAddCondition(e.target.value)} style={{ maxWidth: 200 }}>
                    {Object.entries(CONDITION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="admin-row">
                  <label className="admin-label">Rarity</label>
                  <select className="admin-input" value={addRarity} onChange={(e) => setAddRarity(e.target.value)} style={{ maxWidth: 200 }}>
                    {Object.keys(RARITY_COLORS).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div className="admin-row">
                  <label className="admin-label">StatTrak</label>
                  <label className="admin-toggle">
                    <input type="checkbox" checked={addStattrak} onChange={(e) => setAddStattrak(e.target.checked)} />
                    <span className="admin-toggle-slider"></span>
                  </label>
                </div>
                <div className="admin-row">
                  <label className="admin-label">{t('admin.dropValueLabel')}</label>
                  <input className="admin-input" type="number" step="0.01" value={addValue} onChange={(e) => setAddValue(e.target.value)} style={{ maxWidth: 120 }} placeholder="0.00" />
                </div>
                <div className="admin-row">
                  <label className="admin-label">{t('admin.dropImageUrlLabel')}</label>
                  <input className="admin-input" value={addImageUrl} onChange={(e) => setAddImageUrl(e.target.value)} placeholder="https://..." />
                </div>
                <div className="admin-row">
                  <button className="admin-btn" type="submit" disabled={addLoading}>{addLoading ? t('settings.saving') : t('admin.dropAdd')}</button>
                  <button className="admin-btn admin-btn-secondary" type="button" onClick={() => setShowAdd(false)}>{t('matchDetail.cancel')}</button>
                  {addError && <span className="admin-error">{addError}</span>}
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="admin-info">{t('live.loading')}</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Skin</th>
              <th>Rarity</th>
              <th>Condition</th>
              <th>{t('admin.dropColValue')}</th>
              <th>{t('admin.colStatus')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {skins.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.stattrak && <span style={{ color: '#cf6a32' }}>ST </span>}
                  {s.name}
                </td>
                <td>
                  <span style={{ color: RARITY_COLORS[s.rarity] || 'inherit' }}>
                    {s.rarity}
                  </span>
                </td>
                <td>{CONDITION_LABELS[s.condition] || s.condition || '-'}</td>
                <td>{s.value_sek ? `${s.value_sek} kr` : '-'}</td>
                <td>{s.status || 'available'}</td>
                <td>
                  {s.status === 'available' && (
                    <button
                      className="admin-btn admin-btn-danger"
                      style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                      onClick={() => handleDelete(s.id)}
                    >
                      {t('admin.remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {skins.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{t('admin.dropPoolEmpty')}</td></tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

function DropConfig({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({});

  useEffect(() => {
    const fetchConfig = async () => {
      if (!perms.guildId) return;
      try {
        const r = await fetch(`${API_URL}/api/admin/drops/config/${perms.guildId}`, { headers: getAuthHeaders() });
        if (r.ok) {
          const data = await r.json();
          setConfig(data);
          setForm({
            enabled: data.enabled ?? false,
            drop_chance_percent: data.drop_chance_percent ?? 100,
            weighted_by_rarity: data.weighted_by_rarity ?? false,
            auto_trade_enabled: data.auto_trade_enabled ?? false,
          });
        }
      } catch {} finally { setLoading(false); }
    };
    fetchConfig();
  }, [perms.guildId, getAuthHeaders]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/config/${perms.guildId}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const data = await r.json();
        setError(data.error || data.detail || t('settings.couldNotSave'));
      } else {
        setSaved(true);
        setConfig({ ...config, ...form });
        setTimeout(() => setSaved(false), 3000);
      }
    } catch { setError(t('settings.networkError')); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="admin-info">{t('live.loading')}</div>;
  if (!config) return <div className="admin-info">{t('admin.dropConfigLoadFailed')}</div>;

  return (
    <div className="admin-card">
      <div className="admin-card-title">{t('admin.dropConfigTitle')}</div>

      <div className="admin-row" style={{ justifyContent: 'space-between' }}>
        <span className="admin-label">{t('admin.dropEnabled')}</span>
        <label className="admin-toggle">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          <span className="admin-toggle-slider"></span>
        </label>
      </div>

      <div className="admin-row">
        <span className="admin-label">{t('admin.dropChance')}</span>
        <input className="admin-input" type="number" min="0" max="100" value={form.drop_chance_percent}
          onChange={(e) => setForm({ ...form, drop_chance_percent: parseInt(e.target.value) || 0 })}
          style={{ maxWidth: 100 }} />
      </div>

      <div className="admin-row" style={{ justifyContent: 'space-between' }}>
        <span className="admin-label">{t('admin.dropWeighted')}</span>
        <label className="admin-toggle">
          <input type="checkbox" checked={form.weighted_by_rarity}
            onChange={(e) => setForm({ ...form, weighted_by_rarity: e.target.checked })} />
          <span className="admin-toggle-slider"></span>
        </label>
      </div>

      <div className="admin-row" style={{ justifyContent: 'space-between' }}>
        <span className="admin-label">{t('admin.dropAutoTrade')}</span>
        <label className="admin-toggle">
          <input type="checkbox" checked={form.auto_trade_enabled}
            onChange={(e) => setForm({ ...form, auto_trade_enabled: e.target.checked })} />
          <span className="admin-toggle-slider"></span>
        </label>
      </div>

      {config.has_steam_credentials !== undefined && (
        <div className="admin-row">
          <span className="admin-label">{t('admin.dropSteamCreds')}</span>
          <span style={{ color: config.has_steam_credentials ? 'var(--accent-green)' : 'var(--accent-red)' }}>
            {config.has_steam_credentials ? t('admin.dropCredsConfigured') : t('admin.dropCredsMissing')}
          </span>
        </div>
      )}

      <div className="admin-row" style={{ marginTop: '1rem' }}>
        <button className="admin-btn" onClick={handleSave} disabled={saving}>
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
        {saved && <span className="admin-success">{t('admin.saved')}</span>}
        {error && <span className="admin-error">{error}</span>}
      </div>
    </div>
  );
}

function Trades({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptResult, setAcceptResult] = useState(null);
  const [rerollMatchId, setRerollMatchId] = useState('');
  const [rerollMatchLoading, setRerollMatchLoading] = useState(false);
  const [rerollWinner, setRerollWinner] = useState('');
  const [rerollPlayers, setRerollPlayers] = useState([]);

  const fetchDrops = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/recent?limit=20`, { headers: getAuthHeaders() });
      if (r.ok) {
        const data = await r.json();
        setDrops(Array.isArray(data) ? data : data.results || []);
      }
    } catch {} finally { setLoading(false); }
  }, [getAuthHeaders]);

  const fetchPlayers = useCallback(async () => {
    if (!perms.guildId) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/players/${perms.guildId}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setRerollPlayers(Array.isArray(data) ? data : data.results || []);
      }
    } catch {}
  }, [perms.guildId, getAuthHeaders]);

  useEffect(() => { fetchDrops(); fetchPlayers(); }, [fetchDrops, fetchPlayers]);

  const handleCheckTrades = async () => {
    setCheckLoading(true);
    setCheckResult(null);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/check-trades`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ guild_id: perms.guildId }),
      });
      const data = await r.json();
      if (!r.ok) setError(data.error || data.detail || t('admin.errorGeneric'));
      else { setCheckResult(data); fetchDrops(); }
    } catch { setError(t('settings.networkError')); }
    finally { setCheckLoading(false); }
  };

  const handleAcceptDonations = async () => {
    setAcceptLoading(true);
    setAcceptResult(null);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/accept-donations`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ guild_id: perms.guildId }),
      });
      const data = await r.json();
      if (!r.ok) setError(data.error || data.detail || t('admin.errorGeneric'));
      else setAcceptResult(data);
    } catch { setError(t('settings.networkError')); }
    finally { setAcceptLoading(false); }
  };

  const handleDeliver = async (dropId) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/${dropId}/deliver`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      if (r.ok) fetchDrops();
      else setError(t('admin.dropDeliverFailed'));
    } catch { setError(t('settings.networkError')); }
  };

  const handleReroll = async (dropId) => {
    if (!window.confirm(t('admin.dropRerollConfirm'))) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/${dropId}/reroll`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await r.json();
      if (r.ok) {
        setError('');
        fetchDrops();
        alert(t('admin.dropNewWinner', { winner: data.winner_name, old: data.old_winner }));
      } else {
        setError(data.error || t('admin.dropRerollFailed'));
      }
    } catch { setError(t('settings.networkError')); }
  };

  const handleRerollByMatch = async () => {
    if (!rerollMatchId) return;
    const confirmMsg = rerollWinner
      ? t('admin.dropAssignConfirm', { id: rerollMatchId })
      : t('admin.dropRerollMatchConfirm', { id: rerollMatchId });
    if (!window.confirm(confirmMsg)) return;
    setRerollMatchLoading(true);
    setError('');
    try {
      const body = { match_id: parseInt(rerollMatchId, 10) };
      if (rerollWinner) body.winner_discord_id = rerollWinner;
      const r = await fetch(`${API_URL}/api/admin/drops/reroll-by-match`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (r.ok) {
        fetchDrops();
        setRerollMatchId('');
        setRerollWinner('');
        alert(t('admin.dropNewWinner', { winner: data.winner_name, old: data.old_winner }));
      } else {
        setError(data.error || t('admin.dropRerollFailed'));
      }
    } catch { setError(t('settings.networkError')); }
    finally { setRerollMatchLoading(false); }
  };

  return (
    <>
      {error && <div className="admin-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      <div className="admin-card" style={{ marginBottom: '1rem' }}>
        <div className="admin-card-title">{t('admin.dropRerollTitle')}</div>
        <div className="admin-card-desc">{t('admin.dropRerollDesc')}</div>
        <div className="admin-row">
          <input
            className="admin-input"
            type="number"
            placeholder={t('admin.matchIdPlaceholder')}
            value={rerollMatchId}
            onChange={(e) => setRerollMatchId(e.target.value)}
            style={{ maxWidth: 120 }}
          />
          <select
            className="admin-input"
            value={rerollWinner}
            onChange={(e) => setRerollWinner(e.target.value)}
          >
            <option value="">{t('admin.dropRandomWinner')}</option>
            {rerollPlayers.map((p) => (
              <option key={p.id} value={p.discord_id || p.discord_user_id}>
                {p.steam_name || p.steam_username || p.discord_name || p.discord_username || p.name || p.id}
              </option>
            ))}
          </select>
          <button
            className="admin-btn admin-btn-danger"
            onClick={handleRerollByMatch}
            disabled={!rerollMatchId || rerollMatchLoading}
          >
            {rerollMatchLoading ? t('admin.dropRerolling') : rerollWinner ? t('admin.dropAssign') : t('admin.dropReroll')}
          </button>
        </div>
      </div>

      <div className="admin-row" style={{ marginBottom: '1rem' }}>
        <button className="admin-btn" onClick={handleCheckTrades} disabled={checkLoading}>
          {checkLoading ? t('admin.dropCheckingTrades') : t('admin.dropCheckTrades')}
        </button>
        <button className="admin-btn admin-btn-secondary" onClick={handleAcceptDonations} disabled={acceptLoading}>
          {acceptLoading ? t('admin.dropAccepting') : t('admin.dropAcceptDonations')}
        </button>
        {checkResult && <span className="admin-success">{t('admin.matchDone')}</span>}
        {acceptResult && <span className="admin-success">{t('admin.dropAcceptedCount', { count: acceptResult.accepted ?? 0 })}</span>}
      </div>

      {loading ? (
        <div className="admin-info">{t('live.loading')}</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Drop</th>
              <th>Skin</th>
              <th>{t('admin.dropColWinner')}</th>
              <th>{t('admin.dropColTradeStatus')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {drops.map((d) => (
              <tr key={d.id}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{d.id}</td>
                <td>{d.prize?.name || '-'}</td>
                <td>{d.winner?.steam_user?.username || d.winner?.discord_user?.username || '-'}</td>
                <td>{d.trade_status || d.status || '-'}</td>
                <td className="admin-row" style={{ border: 'none', marginBottom: 0 }}>
                  {d.trade_status !== 'delivered' && d.trade_status !== 'accepted' && (
                    <>
                      <button
                        className="admin-btn admin-btn-secondary"
                        style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                        onClick={() => handleDeliver(d.id)}
                      >
                        {t('admin.dropDelivered')}
                      </button>
                      <button
                        className="admin-btn admin-btn-danger"
                        style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                        onClick={() => handleReroll(d.id)}
                      >
                        {t('admin.dropReroll')}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {drops.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{t('admin.dropTradesEmpty')}</td></tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

function BotInventory({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [regLoading, setRegLoading] = useState(false);
  const [regResult, setRegResult] = useState(null);
  // PR T: auto-register state. Knappen synkar bot-inventory mot SkinPrize-poolen
  // utan att kräva manuell registrering per skin. Återanvänder api-containerns
  // cachade Steam-session så ingen ny login krävs vid varje klick.
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const fetchInventory = useCallback(async () => {
    if (!perms.guildId) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/bot-inventory?guild_id=${perms.guildId}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setItems(Array.isArray(data) ? data : data.items || []);
      } else {
        let detail = '';
        try {
          const data = await r.json();
          detail = data.detail || data.error || '';
        } catch {}
        if (detail.toLowerCase().includes('credentials') || r.status === 400) {
          setError(t('admin.dropNoCreds'));
        } else if (r.status === 503) {
          setError(t('admin.dropSteamUnavailable'));
        } else {
          setError(detail || t('admin.dropInvFetchFailed', { status: r.status }));
        }
      }
    } catch { setError(t('admin.dropBackendError')); }
    finally { setLoading(false); }
  }, [perms.guildId, getAuthHeaders, t]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handleAutoRegister = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/auto-register`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ guild_id: perms.guildId }),
      });
      const data = await r.json();
      if (!r.ok) setError(data.error || data.detail || t('admin.dropSyncFailed'));
      else {
        setSyncResult(data);
        await fetchInventory();
      }
    } catch { setError(t('settings.networkError')); }
    finally { setSyncLoading(false); }
  };

  const toggleItem = (assetId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const handleRegister = async () => {
    setRegLoading(true);
    setRegResult(null);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/skins/from-inventory`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guild_id: perms.guildId,
          asset_ids: Array.from(selected),
        }),
      });
      const data = await r.json();
      if (!r.ok) setError(data.error || data.detail || t('admin.dropRegisterFailed'));
      else { setRegResult(data); setSelected(new Set()); }
    } catch { setError(t('settings.networkError')); }
    finally { setRegLoading(false); }
  };

  /**
   * Mappar pool_status från backend till en visuell badge.
   * PR T: backend taggar varje item med en status så admin ser hela inventoryt
   * inklusive items som auto_register_inventory tagit in men som fortfarande
   * är trade-hold:ade och inte kan väljas som drops än.
   */
  const STATUS_BADGES = {
    in_pool: { label: t('admin.dropBadgeInPool'), color: 'var(--accent-green)' },
    on_hold: { label: t('admin.dropBadgeOnHold'), color: 'var(--accent-orange)' },
    unregistered_tradeable: { label: t('admin.dropBadgeUnregistered'), color: 'var(--accent-blue)' },
    unregistered_on_hold: { label: t('admin.dropBadgeUnregOnHold'), color: 'var(--text-muted)' },
  };

  const formatTradeableAt = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(currentLocale());
    } catch { return ''; }
  };

  return (
    <>
      {error && <div className="admin-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      <div className="admin-row" style={{ marginBottom: '1rem', gap: '0.75rem' }}>
        <button
          className="admin-btn"
          onClick={handleAutoRegister}
          disabled={syncLoading || !perms.guildId}
          title={t('admin.dropSyncTitle')}
        >
          {syncLoading ? t('admin.dropSyncing') : t('admin.dropSyncInventory')}
        </button>
        {syncResult && (
          <span className="admin-success">
            {t('admin.dropSyncResult', { created: syncResult.created, updated: syncResult.updated, skipped: syncResult.skipped })}
          </span>
        )}
      </div>

      {selected.size > 0 && (
        <div className="admin-row" style={{ marginBottom: '1rem' }}>
          <button className="admin-btn" onClick={handleRegister} disabled={regLoading}>
            {regLoading ? t('admin.dropRegistering') : t('admin.dropRegisterSelected', { count: selected.size })}
          </button>
          {regResult && <span className="admin-success">{t('admin.dropRegisteredCount', { count: regResult.registered ?? regResult.count ?? 0 })}</span>}
        </div>
      )}

      {loading ? (
        <div className="admin-info">{t('admin.dropLoadingInventory')}</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th>Skin</th>
              <th>{t('admin.colStatus')}</th>
              <th>Tradeable</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const assetId = item.asset_id || item.assetid || item.id;
              const status = item.pool_status || (item.skin_prize_id ? 'in_pool' : 'unregistered_tradeable');
              const badge = STATUS_BADGES[status] || STATUS_BADGES.unregistered_tradeable;
              const isUnregistered = status === 'unregistered_tradeable' || status === 'unregistered_on_hold';
              const opacity = status === 'on_hold' || status === 'unregistered_on_hold' ? 0.6 : 1;
              return (
                <tr key={assetId} style={{ opacity }}>
                  <td>
                    {isUnregistered && (
                      <input
                        type="checkbox"
                        checked={selected.has(assetId)}
                        onChange={() => toggleItem(assetId)}
                      />
                    )}
                  </td>
                  <td>{item.market_hash_name || item.name || '-'}</td>
                  <td>
                    <span style={{ color: badge.color, fontWeight: 600 }}>{badge.label}</span>
                  </td>
                  <td>{item.tradeable_at ? formatTradeableAt(item.tradeable_at) : (item.tradable ? t('admin.dropYes') : '-')}</td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{t('admin.dropInventoryEmpty')}</td></tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

/**
 * Exclusions tab — manage players excluded from winning drops.
 * Allows adding/removing players from the exclusion list.
 */
function Exclusions({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [excluded, setExcluded] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchExcluded = useCallback(async () => {
    if (!perms.guildId) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/excluded/${perms.guildId}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setExcluded(Array.isArray(data) ? data : []);
      }
    } catch {} finally { setLoading(false); }
  }, [perms.guildId, getAuthHeaders]);

  const fetchPlayers = useCallback(async () => {
    if (!perms.guildId) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/players/${perms.guildId}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setAllPlayers(list);
      }
    } catch {}
  }, [perms.guildId, getAuthHeaders]);

  useEffect(() => {
    fetchExcluded();
    fetchPlayers();
  }, [fetchExcluded, fetchPlayers]);

  const handleExclude = async () => {
    if (!selectedPlayer) return;
    setActionLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/excluded/${perms.guildId}/exclude`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: selectedPlayer }),
      });
      const data = await r.json();
      if (!r.ok) setError(data.error || t('admin.dropExcludeFailed'));
      else {
        setSelectedPlayer('');
        fetchExcluded();
      }
    } catch { setError(t('settings.networkError')); }
    finally { setActionLoading(false); }
  };

  const handleUnexclude = async (playerId) => {
    setActionLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/drops/excluded/${perms.guildId}/unexclude`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId }),
      });
      const data = await r.json();
      if (!r.ok) setError(data.error || t('admin.dropUnexcludeFailed'));
      else fetchExcluded();
    } catch { setError(t('settings.networkError')); }
    finally { setActionLoading(false); }
  };

  const excludedIds = new Set(excluded.map((p) => p.id));
  const availablePlayers = allPlayers.filter((p) => !excludedIds.has(p.id));

  const getPlayerName = (p) => {
    return p.steam_name || p.steam_username || p.discord_name || p.discord_username || p.name || p.id;
  };

  return (
    <>
      {error && <div className="admin-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      <div className="admin-card">
        <div className="admin-card-title">{t('admin.dropExcludeTitle')}</div>
        <div className="admin-card-desc">
          {t('admin.dropExcludeDesc')}
        </div>
        <div className="admin-row">
          <select
            className="admin-input"
            value={selectedPlayer}
            onChange={(e) => setSelectedPlayer(e.target.value)}
          >
            <option value="">{t('admin.dropSelectPlayer')}</option>
            {availablePlayers.map((p) => (
              <option key={p.id} value={p.id}>{getPlayerName(p)}</option>
            ))}
          </select>
          <button
            className="admin-btn"
            onClick={handleExclude}
            disabled={!selectedPlayer || actionLoading}
          >
            {actionLoading ? t('settings.saving') : t('admin.dropExclude')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="admin-info">{t('live.loading')}</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('nav.players')}</th>
              <th>Discord</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {excluded.map((p) => (
              <tr key={p.id}>
                <td>{p.steam_name || '-'}</td>
                <td>{p.discord_name || '-'}</td>
                <td>
                  <button
                    className="admin-btn admin-btn-danger"
                    style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                    onClick={() => handleUnexclude(p.id)}
                    disabled={actionLoading}
                  >
                    {t('admin.remove')}
                  </button>
                </td>
              </tr>
            ))}
            {excluded.length === 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  {t('admin.dropExclusionsEmpty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}
